// @flow

// Hocs
import ResetPasswordContainer from '../containers/ResetPassword'

// Components
import { Errors } from 'hfty-components-errors'
import Textfield from '@react-mdc/textfield'
import FormField from '@react-mdc/form-field'
import Button from '@react-mdc/button'
import { Link } from 'react-router-dom'

// Libs
import React, { Component } from 'react'
import _size from 'lodash/size'
import _has from 'lodash/has'

// Constants
import { NEW_PASSWORD_PROP } from 'hfty-auth/src/constants'

// Utils
import traverse from 'fc-utils/traverse'

// Translations
import messages from '../messages'
import { FormattedMessage } from 'react-intl'

// Images
import logoImg from 'hfty-images/src/FE_Soils_Logo_WhiteBg.png'

// Styles
import { white, primary, grey } from 'hfty-styles/src/colors'

// Local Types
type Props = {
  operation: ?Object,
  actions: {
    onSubmit: Function,
    onChange: Function,
  },
}

// Styles
const styles = {
  container: {
    flex: '1 1 auto',
    display: 'flex',
    backgroundColor: '#fafafa',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#333',
    flexDirection: 'column',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 5,
    padding: '0 10px',
    flex: '1 1 auto',
    minHeight: '6vh',
    minWidth: '23vh',
    color: grey(),
  },
  textField: {
    marginBottom: '20px',
    width: '80%',
    textAlign: 'center',
  },
  textFieldError: {
    color: '#F4537F',
    fontSize: 12,
    marginTop: 10,
  },
  component: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    margin: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: '#ffffff',
    backgroundColor: white(),
    padding: 20,
    width: '30%',
    minWidth: 350,
    boxShadow: '0 0 3px rgba(0,0,0,0.3)',
    flex: '1 1 auto',
    justifyContent: 'space-around',
  },
  successMessage: {
    display: 'flex',
    flexWrap: 'wrap',
    color: primary(),
  },
  span: {
    display: 'inline-flex',
    marginTop: 10,
  },
  link: {
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  resetPassword: {
    fontSize: 12,
    padding: 6,
  },
  banner: {
    display: 'block',
    background: primary(),
    position: 'absolute',
    top: 0,
    left: 0,
    height: '4.4rem',
    width: '100%',
  },
  logo: {
    display: 'block',
    margin: 'auto',
    height: '3rem',
    position: 'relative',
    top: '18%',
  },
  formField: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  divSpace: (errors: boolean): Object => ({
    marginLeft: 5,
    color: errors ? '#F4537F' : '#979797',
    fontSize: '12px',
  }),
}

const getPasswordLabel = traverse(['operation', 'model', NEW_PASSWORD_PROP], { default: '' })

// Exports
export class ResetPasswordForm extends Component {
  props: Props
  onChange: Function

  constructor(props: Props) {
    super(props)

    this.state = {
      passwordIsLessEightChars: false,
      fieldError: null,
      confirmPassword: '',
    }

    this.onChange = this.onChange.bind(this)
    this.onChangeConfirmPassword = this.onChangeConfirmPassword.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
  }

  onChange(e: Event & { currentTarget: HTMLInputElement }) {
    this.props.actions.onChange({
      ...this.props.operation,
      model: {
        ...this.props.operation.model,
        [NEW_PASSWORD_PROP]: e.currentTarget.value,
      },
    })
  }

  onChangeConfirmPassword(e: Event & { currentTarget: HTMLInputElement }) {
    this.setState({
      confirmPassword: e.currentTarget.value,
    })
  }

  onSubmit(e: Event & { currentTarget: HTMLInputElement }) {
    e.preventDefault()
    let { model } = this.props.operation

    if (!model || (model.password && model.password.length < 8)) {
      this.setState({
        passwordIsLessEightChars: true,
      })
    } else if (model.password) {
      if (model.password !== this.state.confirmPassword) {
        this.setState({
          fieldError: 'passwordDoNotMatch',
          passwordIsLessEightChars: false,
        })
      } else {
        this.setState({ fieldError: null, passwordIsLessEightChars: false })
        this.props.actions.onSubmit()
      }
    }
  }

  renderForm(): ?React.Element<any> {
    return (
      <div>
        <div style={styles.banner}>
          <img src={logoImg} style={styles.logo} />
        </div>

        <form style={styles.form} onSubmit={this.onSubmit}>
          <div style={styles.title}>
            <p>
              <FormattedMessage {...messages['setPassword']} />
            </p>
          </div>
          <Errors errors={this.props.operation.errors} messages={messages} />
          {this.renderRedirectNewPassword()}
          <div style={styles.textFieldError}>
            {this.state.fieldError ? <FormattedMessage {...messages['passwordsDoNotMatch']} /> : null}
          </div>
          <div style={styles.textField}>
            <FormField style={styles.formField}>
              <Textfield style={{ width: '100%' }}>
                <Textfield.Label htmlFor="password">
                  <FormattedMessage {...messages['newPassword']} />
                </Textfield.Label>
                <Textfield.Input
                  id={'password'}
                  type="password"
                  value={getPasswordLabel(this.props)}
                  onChange={this.onChange}
                />
              </Textfield>
            </FormField>
          </div>
          {this.state.passwordIsLessEightChars && (
            <div style={styles.divSpace(this.state.passwordIsLessEightChars)}>
              <FormattedMessage {...messages['passwordMustBeAtLeast8Chars']} />
            </div>
          )}
          <div style={styles.textField}>
            <FormField style={styles.formField}>
              <Textfield style={{ width: '100%' }}>
                <Textfield.Label htmlFor="confirmPassword">
                  <FormattedMessage {...messages['confirmPassword']} />
                </Textfield.Label>
                <Textfield.Input
                  id={'confirmPassword'}
                  type="password"
                  value={this.state.confirmPassword}
                  onChange={this.onChangeConfirmPassword}
                />
              </Textfield>
            </FormField>
          </div>
          <FormField>
            <Button raised primary onClick={this.onSubmit}>
              <FormattedMessage {...messages['changePassword']} />
            </Button>
          </FormField>
        </form>
      </div>
    )
  }

  renderRedirectNewPassword(): ?React.Element<any> {
    if (_size(this.props.operation.errors)) {
      if (_has(this.props.operation.errors[0], 'expiredPasswordLink')) {
        return (
          <div style={styles.resetPassword}>
            <Link style={styles.link} to={'/forgotpassword'}>
              <FormattedMessage {...messages['redirectNewPasswordLink']} />
            </Link>
          </div>
        )
      }
    }
  }

  renderSuccessMessage(): ?React.Element<any> {
    return (
      <div style={styles.form}>
        <p style={styles.successMessage}>
          <FormattedMessage {...messages['passwordResetSuccess']} />
        </p>
        <Link style={styles.link} to={'/'}>
          <FormattedMessage {...messages['goToLoginPage']} />
        </Link>
      </div>
    )
  }

  render(): ?React.Element<any> {
    return (
      <div style={styles.container}>
        <div style={styles.component}>
          {this.props.operation
            ? !this.props.operation.success
              ? this.renderForm()
              : this.renderSuccessMessage()
            : null}
        </div>
      </div>
    )
  }
}

export default ResetPasswordContainer(ResetPasswordForm)
